import { Button, Stack, TextField, TextFieldProps, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import React, { memo, useCallback, useContext, useRef, useState } from "react";
import { DashboardContext } from "../../../contexts/DashboardContext";
import { scrollbarStyle } from "../../../shared/dashboard";

const useStyles = makeStyles((theme) => ({
    card: {
        position: 'relative',
        width: 300,
        height: 'fit-content',
        padding: 16,
        borderRadius: 16,
        backgroundColor: '#fff',
        border: `1px solid ${theme.colors.neutral['100']}`,
        boxShadow: '0px 3px 5px -1px rgba(16, 24, 40,0.2), 0px 5px 8px 0px rgba(16, 24, 40,0.14), 0px 1px 14px 0px rgba(16, 24, 40,0.12)',
        transition: 'all 300ms ease-in-out',
    },
    container: {
        display: 'block',
        width: '100%',
        height: '100%',
        padding: 8,
        borderRadius: 12,
        border: `1px solid ${theme.colors.neutral['300']}`,
        background: '#fff',
        transition: 'all 0.2s ease',
        breakInside: 'avoid',
        overflowX: 'hidden',
        overflowY: 'auto',
        ...scrollbarStyle,
        "&:hover": {
            border: `1px solid ${theme.colors.primary['300']}`,
            boxShadow: '0px 1px 5px rgba(0, 0, 0, 0.12)',
        },
    },
    title: {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        wordBreak: 'break-word',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontFamily: 'Poppins',
        fontSize: '1rem',
        fontWeight: '600',
        color: '#666666',
    },
    inputField: {
        width: '100%',
        "& .MuiOutlinedInput-root": {
            width: '100%',
            padding: 'unset',
            "& > textarea": {
                fontFamily: 'Inter',
                fontSize: '1rem',
                padding: 'unset',
            },
        },
    },
    cancelButton: {
        borderRadius: 40,
        borderColor: theme.colors.neutral['100'],
        background: theme.colors.neutral['100'],
        color: theme.colors.neutral['500'],
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
    },
    saveButton: {
        borderRadius: 40,
        backgroundColor: theme.colors.primary['600'],
        color: 'white',
        fontWeight: 'bold',
        textTransform: 'none',
        transition: 'ease-in-out 300ms',
        '&:hover': {
            backgroundColor: theme.colors.primary['500'],
        }
    },
}));

const CopyPasteContentCard: React.FC<{
    onSave?: (newTags: string[]) => void,
    onClose?: () => void,
}> = ({ onSave, onClose }) => {
    const classes = useStyles();
    const { isPublicView } = useContext(DashboardContext);
    const [saveReady, setSaveReady] = useState<boolean>(false);
    const inputValue = useRef<TextFieldProps>(null);

    const handleSave = useCallback(() => {
        if (inputValue.current?.value) {
            const newTags = (inputValue.current?.value as string).split('\n').map(t => t.trim()).filter(t => t);

            onSave?.(newTags);
            setSaveReady(false);
            onClose?.();
        }
    }, [onSave, onClose]);

    return (<>
        <Stack direction="column" className={classes.card} spacing={2} justifyContent="stretch">
            <Typography className={classes.title}>
                {'Copy/paste from list:'}
            </Typography>
            <Stack direction="column" spacing={2} alignItems="flex-start" width="100%">
                <Stack className={classes.container} width="100%">
                    <TextField variant="outlined"
                        className={classNames('no-border', classes.inputField)}
                        inputProps={{ className: 'no-fade', style: {
                            width: '100%',
                            whiteSpace: saveReady ? 'nowrap' : 'pre-wrap',
                            overflowX: 'auto',
                        }}} placeholder={!isPublicView ? 'Tag1\nTag2\nTag3' : ''}
                        onChange={() => setSaveReady(!!inputValue.current?.value)}
                        inputRef={inputValue}
                        disabled={isPublicView}
                        rows={7}
                        multiline fullWidth/>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%">
                    <Button variant="outlined" size="medium"
                        className={classes.cancelButton}
                        onClick={onClose}
                    > {'Cancel'} </Button>
                    <Button variant="contained" size="medium"
                        className={classes.saveButton}
                        disabled={!saveReady}
                        onClick={handleSave}
                    > {'Add'} </Button>
                </Stack>
            </Stack>
        </Stack>
    </>);
};

export default memo(CopyPasteContentCard);
